import { FC, MouseEvent } from 'react';
import { TooltipRenderProps } from 'react-joyride';
import { Box, Typography, Button, IconButton } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../../../../atomic/atoms';
import './AIGenerationTourTooltip.scss';

export const AIGenerationTourTooltip: FC<TooltipRenderProps> = ({
    backProps,
    closeProps,
    continuous,
    index,
    isLastStep,
    primaryProps,
    skipProps,
    step,
    size,
    tooltipProps,
}) => {
    const { t } = useTranslation();
    const progress = ((index + 1) / size) * 100;
    const nextLabel = isLastStep
        ? t('mannequin.aiGeneration.tour.finish')
        : t('mannequin.aiGeneration.tour.next');

    const handleNextClick = () => {
        primaryProps.onClick?.({
            preventDefault: () => undefined,
            stopPropagation: () => undefined,
        } as MouseEvent<HTMLElement>);
    };

    return (
        <Box {...tooltipProps} className="ai-gen-tour-tooltip">
            <Box className="ai-gen-tour-tooltip__progress-track" aria-hidden="true">
                <Box
                    className="ai-gen-tour-tooltip__progress-fill"
                    sx={{ width: `${progress}%` }}
                />
            </Box>
            <Box className="ai-gen-tour-tooltip__body">
                <Box className="ai-gen-tour-tooltip__header">
                    <Box className="ai-gen-tour-tooltip__step-badge">
                        {t('mannequin.aiGeneration.tour.stepProgress', {
                            current: index + 1,
                            total: size,
                        })}
                    </Box>
                    <IconButton
                        {...closeProps}
                        size="small"
                        className="ai-gen-tour-tooltip__close"
                        aria-label={t('mannequin.aiGeneration.tour.close')}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>

                {step.title && (
                    <Typography component="h3" className="ai-gen-tour-tooltip__title">
                        {step.title}
                    </Typography>
                )}

                <Typography component="p" className="ai-gen-tour-tooltip__content">
                    {step.content}
                </Typography>

                <Box className="ai-gen-tour-tooltip__footer">
                    {!isLastStep && (
                        <Button
                            {...skipProps}
                            variant="text"
                            size="small"
                            className="ai-gen-tour-tooltip__skip"
                        >
                            {t('mannequin.aiGeneration.tour.skip')}
                        </Button>
                    )}
                    <Box className="ai-gen-tour-tooltip__footer-spacer" />
                    {index > 0 && (
                        <Button
                            {...backProps}
                            variant="outlined"
                            size="small"
                            className="ai-gen-tour-tooltip__back"
                        >
                            {t('mannequin.aiGeneration.tour.back')}
                        </Button>
                    )}
                    {continuous && (
                        <SCUIButton
                            type="primary"
                            size="m"
                            sizeType="default"
                            buttonType="button"
                            text={nextLabel}
                            onClick={handleNextClick}
                            className="ai-gen-tour-tooltip__next"
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};
